import ThemeControl from "@/components/ui/ThemeControl";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import usePageMotion from "../../hooks/usePageMotion";
import GradientNavHp from "../../components/ui/gradient-nav";
import { useQueryClient } from "@tanstack/react-query";
import { LayoutDashboard, Megaphone, Users, Rss, Radio, Users2, Ticket, CalendarDays, Trophy, FileUp, Wallet, Receipt, Crown, Menu, X, LogOut, ArrowLeft, ListChecks } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

// Sidebar khusus Marketing (brief owner 12 Agu poin 4): sidebar utama (Layout.jsx)
// cuma punya SATU entri "Marketing" — 13 menu di bawah ini pindah ke sini, persis
// pola Finance (FinanceLayout.jsx). URL-nya TIDAK berubah supaya bookmark lama tetap
// jalan; `tid` tiap item juga dipertahankan sama persis dengan yang dulu ada di
// Layout.jsx karena ada tes yang mengunci nama data-testid itu.
const NAV = [
  { section: "Ringkasan", items: [
    { to: "/marketing/dashboard", end: true, icon: LayoutDashboard, label: "Dashboard Marketing", tid: "nav-mkt-dashboard", modKey: "marketing_dashboard" },
    // Dashboard CEO = agregat margin/ROI/laba lintas brand — SENGAJA tidak ikut
    // preset "Admin Marketing" (backend/deps.py PERMISSION_TEMPLATES) dan TIDAK
    // ikut dilonggarkan oleh master toggle `marketing` (authorize_module/can()).
    // Filter di bawah (`can(modKey, "view")`) sudah menegakkan itu sendiri — item
    // ini hanya tampil kalau `marketing_executive` benar-benar di-assign eksplisit.
    { to: "/marketing/executive", icon: Crown, label: "Dashboard CEO", tid: "nav-mkt-executive", modKey: "marketing_executive" },
  ]},
  { section: "Kampanye & Konten", items: [
    { to: "/marketing/campaigns", icon: Megaphone, label: "Campaign", tid: "nav-mkt-campaign", modKey: "marketing_campaign" },
    { to: "/marketing/content", icon: CalendarDays, label: "Kalender Konten", tid: "nav-mkt-content", modKey: "marketing_content" },
    { to: "/marketing/promo-codes", icon: Ticket, label: "Kode Promo", tid: "nav-mkt-promo", modKey: "marketing_promo" },
  ]},
  { section: "KOL & Promosi", items: [
    { to: "/marketing/kol", icon: Users, label: "KOL & Influencer", tid: "nav-mkt-kol", modKey: "marketing_kol" },
    // "Skor KOL" menumpang izin `marketing_kol` — sama alasan seperti dulu di
    // Layout.jsx: bagian dari pekerjaan KOL, bukan modul terpisah.
    { to: "/marketing/kol-score", icon: Trophy, label: "Skor KOL", tid: "nav-mkt-kol-score", modKey: "marketing_kol" },
    { to: "/marketing/pfp", icon: Rss, label: "PFP & Paid Promote", tid: "nav-mkt-pfp", modKey: "marketing_pfp" },
  ]},
  { section: "Iklan & Lead", items: [
    { to: "/marketing/ads", icon: Radio, label: "Paid Ads", tid: "nav-mkt-ads", modKey: "marketing_ads" },
    // "Impor CSV Iklan" menumpang izin `marketing_ads` juga (jalur masuk data, bukan modul baru).
    { to: "/marketing/ads-import", icon: FileUp, label: "Impor CSV Iklan", tid: "nav-mkt-ads-import", modKey: "marketing_ads" },
    { to: "/marketing/leads", icon: Users2, label: "Lead & Atribusi", tid: "nav-mkt-lead", modKey: "marketing_lead" },
  ]},
  { section: "Anggaran", items: [
    { to: "/marketing/budget", icon: Wallet, label: "Anggaran Marketing", tid: "nav-mkt-budget", modKey: "marketing_budget" },
    { to: "/marketing/expense", icon: Receipt, label: "Beban Marketing", tid: "nav-mkt-expense", modKey: "marketing_expense" },
    // `selaluTampil`: Task adalah satu-satunya halaman Marketing yang memang
    // ditujukan untuk SETIAP user (brief owner — lihat pengecualian yang sama di
    // `App.js::MarketingGate`). Staf yang dikirimi tugas tidak punya izin modul
    // marketing apa pun; kalau item ini ikut disaring `can()`, mereka tidak
    // punya satu pun jalan menuju tugasnya sendiri. Backend tetap membatasi isi
    // yang terlihat ke task miliknya (`_batas_akses_task`).
    { to: "/marketing/tasks", icon: ListChecks, label: "Task / Daily Mission", tid: "nav-mkt-task", modKey: "marketing_task", selaluTampil: true },
  ]},
];

export default function MarketingLayout() {
  // branding dari AuthContext — jangan fetch /config/branding sendiri di sini,
  // sama seperti FinanceLayout (satu-satunya pemanggil ada di AuthProvider).
  const { user, logout, can, branding } = useAuth();
  const nav = useNavigate();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const pageRef = useRef(null);
  usePageMotion(pageRef, location.pathname + location.search);
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // Bersihkan cache React Query sebelum keluar — sama dengan doLogout di
  // Layout.jsx/FinanceLayout.jsx, supaya akun berikutnya di tab yang sama tidak
  // sekilas memegang data cache milik akun sebelumnya.
  const doLogout = () => { queryClient.clear(); logout(); nav("/login"); };
  // Filter NAV: hanya tampilkan item yang user boleh lihat. `can()` di AuthContext
  // sudah menghormati master toggle `marketing` DAN pengecualian `marketing_margin`
  // (lihat komentar di context/AuthContext.jsx) — tidak perlu logika tambahan di sini.
  const visibleNav = NAV
    .map(sec => ({ ...sec, items: sec.items.filter(it => it.selaluTampil || can(it.modKey, "view")) }))
    .filter(sec => sec.items.length > 0);

  return (
    <div className="eh-workspace min-h-screen flex bg-[#F8F9FA]">
      <aside
        data-testid="marketing-sidebar"
        className={`eh-sidebar fixed lg:sticky top-0 left-0 z-40 w-64 h-screen shrink-0 bg-[#0F172A] text-white flex flex-col transition-transform duration-300 lg:translate-x-0 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shrink-0 shadow-lg shadow-emerald-500/25">
              <Megaphone className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-display font-bold text-sm leading-tight truncate">Marketing</p>
              <p className="text-[11px] text-white/50">{branding.app_name || "EppleHub Operational System"}</p>
            </div>
          </div>
          <button onClick={() => setDrawerOpen(false)} data-testid="mkt-close-drawer" className="lg:hidden p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-3 space-y-4 overflow-y-auto">
          {/* Dipindah ke atas (brief owner 19 Agu poin 7) — dulu duduk di footer
              dekat tombol Keluar, di bawah SEMUA menu Marketing, jadi staf yang cuma
              mampir sebentar harus scroll melewati seluruh sidebar untuk kembali ke
              HRIS. Sekarang jadi hal pertama yang terlihat begitu sidebar dibuka.
              Tidak ada role keluarga "marketing_*" seperti finance_* — semua akun
              yang masuk ke sini adalah akun HRIS biasa, jadi tombol ini selalu
              relevan (beda dari FinanceLayout yang menyembunyikannya untuk role
              finance_* murni). */}
          <button
            data-testid="mkt-back-hris"
            onClick={() => nav("/")}
            className="w-full inline-flex items-center gap-2 px-3 h-9 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-sm transition-colors border border-white/10"
          >
            <ArrowLeft className="w-4 h-4" />
            Kembali ke HRIS
          </button>
          {visibleNav.map(({ section, items }) => (
            <div key={section}>
              <p className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">{section}</p>
              <div className="space-y-0.5">
                {items.map(({ to, end, icon: Icon, label, tid }) => (
                  <NavLink
                    key={to} to={to} end={end} data-testid={tid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        isActive
                          ? "bg-white/10 text-white font-medium"
                          : "text-white/60 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-white/10 space-y-1.5">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="w-9 h-9 rounded-full bg-emerald-600/20 text-emerald-300 grid place-items-center text-sm font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[11px] text-white/50 capitalize">{user?.role?.replace(/_/g, " ")}</p>
            </div>
          </div>
          <ThemeControl />
          <button
            data-testid="mkt-btn-logout"
            onClick={() => { if (confirm("Keluar dari akun?")) doLogout(); }}
            className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-rose-600/10 hover:bg-rose-600/25 text-rose-300 hover:text-rose-100 text-sm font-medium transition-colors border border-rose-500/20"
          >
            <LogOut className="w-4 h-4" />
            Keluar
          </button>
        </div>
      </aside>

      {drawerOpen && <div onClick={() => setDrawerOpen(false)} className="eh-shell-backdrop fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden" />}

      <div className="eh-workspace-body flex-1 flex flex-col min-w-0">
        <header className="eh-topbar lg:hidden sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center gap-3 px-4 py-3">
          <button onClick={() => setDrawerOpen(true)} data-testid="mkt-open-drawer" className="p-2 -ml-2 rounded-md hover:bg-slate-100 text-slate-700">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center shrink-0">
              <Megaphone className="w-4 h-4 text-white" />
            </div>
            <p className="font-display font-bold text-sm text-slate-900 truncate">Marketing</p>
          </div>
        </header>
        <main ref={pageRef} className="eh-content flex-1 overflow-x-hidden" data-testid="marketing-page-content">
          <Outlet />
          <GradientNavHp />
        </main>
      </div>
    </div>
  );
}
